import { createContext, useContext, useId } from 'react';
import type { JSX, ReactNode } from 'react';
import { cx } from './styles';

/**
 * 字段外壳与控件之间的契约。
 *
 * 为什么非得用 context：`aria-describedby` / `aria-invalid` / `required` 必须
 * "外壳知道、控件也知道"，而且是同一份值。让每个调用方自己拼 id 的写法在真实
 * 代码里只有一个结果 —— 总有人漏写，于是读屏器读不出错误原因，"红色边框"成了
 * 唯一的错误通道。
 */
export interface FieldContextValue {
  controlId: string;
  /** 空格分隔的 id 列表；没有可描述内容时为 undefined */
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
  disabled: boolean;
}

const FieldContext = createContext<FieldContextValue | null>(null);

/** 控件侧读取接线信息。脱离 `Field` 使用会拿到 `null`（此时控件按自带属性工作） */
export function useFieldAria(): FieldContextValue | null {
  return useContext(FieldContext);
}

/*
 * React 的 `useId` 会产出 `:r1:` 这种带冒号的值。id 本身合法，但它会进
 * `aria-describedby`，在某些 CSS/querySelector 场景需要转义。去掉冒号省掉这类意外。
 */
function useSanitizedId(): string {
  return useId().replaceAll(':', '');
}

export interface FieldProps {
  label: ReactNode;
  children: ReactNode;
  /** 常驻说明：单位、取值范围、格式要求 */
  hint?: ReactNode;
  /** 一旦给出，字段进入非法态：控件的 `aria-invalid` 与错误文案的 `aria-describedby` 自动接上 */
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** 想自己控制 id（例如把 label 写在别处）时才传；在 Field 里不要给控件再传 id */
  id?: string;
  /** 视觉上隐藏标签（例如搜索框），读屏器仍然读得到 */
  hideLabel?: boolean;
  className?: string;
}

export function Field({
  label,
  children,
  hint,
  error,
  required = false,
  disabled = false,
  id,
  hideLabel = false,
  className,
}: FieldProps): JSX.Element {
  const generatedId = useSanitizedId();
  const controlId = id ?? `field-${generatedId}`;
  const hintId = `${controlId}-hint`;
  const errorId = `${controlId}-error`;
  const invalid = error !== undefined;

  /*
   * 错误优先于说明：读屏器按顺序念，先说"哪里错了"，再说"这项是干什么的"。
   * 宁可让用户听两段，也不要让他听完说明才发现错误被吞在后面。
   */
  const describedBy = invalid
    ? hint === undefined
      ? errorId
      : `${errorId} ${hintId}`
    : hint === undefined
      ? undefined
      : hintId;

  return (
    <FieldContext.Provider value={{ controlId, describedBy, invalid, required, disabled }}>
      <div className={cx('flex flex-col gap-1', className)}>
        <label
          htmlFor={controlId}
          className={cx('text-xs font-medium text-fg-muted', hideLabel && 'sr-only')}
        >
          {label}
          {required ? (
            <>
              {/* 星号只是给"看得见的人"的速记，读屏器读到的是后面那句完整的话 */}
              <span aria-hidden="true" className="ml-0.5 text-critical">
                *
              </span>
              <span className="sr-only">（必填）</span>
            </>
          ) : null}
        </label>

        {children}

        {/*
          * 说明与错误**同时**保留：错误说明"哪不对"，说明写着"单位/范围"——
          * 用户改错的时候恰恰最需要看到单位和范围。
          */}
        {invalid ? (
          /*
           * `role="alert"` 是有意的：错误往往在用户敲键之后才出现，而且可能出现在
           * 光标**已经不在**的那个字段上（例如提交时才发现）。没有 alert，
           * 用户只会看到页面"什么都没发生"。
           */
          <p id={errorId} role="alert" className="m-0 flex items-start gap-1 text-xs text-critical">
            <span aria-hidden="true">!</span>
            <span>{error}</span>
          </p>
        ) : null}
        {hint === undefined ? null : (
          <p id={hintId} className="m-0 text-xs text-fg-subtle">
            {hint}
          </p>
        )}
      </div>
    </FieldContext.Provider>
  );
}
